import React, { useState } from 'react';
import type { Fixture } from '@shootout-comps/types';
import { toDateParam } from '../utils/fixtureDate';

interface FixtureCalendarProps {
  fixtures: Fixture[];
  onSelectDate: (dateParam: string) => void;
  onOpenFixture: (fixtureId: string) => void;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// Monday-first offset (0 = Monday) for a JS Date whose getDay() is 0=Sunday..6=Saturday.
function mondayOffset(getDay: number): number {
  return (getDay + 6) % 7;
}

export default function FixtureCalendar({ fixtures, onSelectDate, onOpenFixture }: FixtureCalendarProps) {
  const scheduled = fixtures.filter(f => f.scheduledAt);
  const unscheduled = fixtures.filter(f => !f.scheduledAt);

  // Opens on whichever month holds the next upcoming fixture (or the most recent past one if the
  // whole division's schedule is already behind us) — picked once at mount so subsequent reloads
  // (polling, live updates) don't yank the user back to a different month while they're browsing.
  const [cursor, setCursor] = useState<Date>(() => {
    if (scheduled.length === 0) return new Date();
    const now = Date.now();
    const dates = scheduled.map(f => new Date(f.scheduledAt as string)).sort((a, b) => a.getTime() - b.getTime());
    const target = dates.find(d => d.getTime() >= now) ?? dates[dates.length - 1];
    return new Date(target.getFullYear(), target.getMonth(), 1);
  });

  const countByDay = new Map<string, number>();
  for (const f of scheduled) {
    const k = toDateParam(new Date(f.scheduledAt as string));
    countByDay.set(k, (countByDay.get(k) ?? 0) + 1);
  }

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = mondayOffset(firstOfMonth.getDay());
  const totalCells = Math.ceil((leadingBlanks + daysInMonth) / 7) * 7;
  const today = new Date();
  const todayKey = toDateParam(today);

  const monthLabel = cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const navBtn: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px',
    color: 'var(--chalk)', width: '32px', height: '32px', fontSize: '0.9rem', cursor: 'pointer',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <button style={navBtn} onClick={() => setCursor(new Date(year, month - 1, 1))}>‹</button>
        <span style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--gold)' }}>{monthLabel}</span>
        <button style={navBtn} onClick={() => setCursor(new Date(year, month + 1, 1))}>›</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px', marginBottom: '3px' }}>
        {WEEKDAY_LABELS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '0.62rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', padding: '0.2rem 0' }}>
            {d}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '3px' }}>
        {Array.from({ length: totalCells }, (_, i) => {
          const dayNum = i - leadingBlanks + 1;
          if (dayNum < 1 || dayNum > daysInMonth) {
            return <div key={i} style={{ minHeight: '52px', background: 'rgba(255,255,255,0.015)', borderRadius: '6px' }} />;
          }
          const cellDate = new Date(year, month, dayNum);
          const k = toDateParam(cellDate);
          const count = countByDay.get(k) ?? 0;
          const hasFixtures = count > 0;
          const isToday = k === todayKey;
          const Tag = hasFixtures ? 'button' : 'div';
          return (
            <Tag
              key={i}
              onClick={hasFixtures ? () => onSelectDate(k) : undefined}
              title={hasFixtures ? `${count} fixture${count !== 1 ? 's' : ''}` : undefined}
              style={{
                minHeight: '52px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.2rem',
                background: hasFixtures ? 'rgba(201,168,76,0.16)' : 'rgba(255,255,255,0.03)',
                border: isToday ? '1px solid rgba(201,168,76,0.7)' : `1px solid ${hasFixtures ? 'rgba(201,168,76,0.35)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: '6px', padding: '0.25rem', cursor: hasFixtures ? 'pointer' : 'default',
                fontFamily: 'inherit',
              }}
            >
              <span style={{ fontSize: '0.72rem', color: isToday ? 'var(--gold)' : hasFixtures ? 'var(--chalk)' : 'var(--chalk-dim)', fontWeight: isToday || hasFixtures ? 700 : 400 }}>
                {dayNum}
              </span>
              {hasFixtures && (
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--gold)' }} />
              )}
            </Tag>
          );
        })}
      </div>

      {unscheduled.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--chalk-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
            Not yet scheduled ({unscheduled.length})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {unscheduled.map(f => (
              <div
                key={f._id}
                onClick={() => onOpenFixture(f._id)}
                style={{
                  fontSize: '0.78rem', padding: '0.5rem 0.7rem', borderRadius: '6px', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                }}
              >
                {f.homeTeamName} <span style={{ color: 'var(--chalk-dim)' }}>vs</span> {f.awayTeamName}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
